import PropTypes from 'prop-types';
import React, { useContext, useEffect, useRef } from 'react';
import { useHistory } from 'react-router-dom';
import styled from 'styled-components';
import classNames from 'classnames';

// Module components
import ErrorHandler from '../../../_molecules/ErrorHandler';
import TrackPageView from '@tracking/TrackPageView';
import AccountInformationForm from '@organisms/AccountInformationForm';

// Shared components
import StepperNavMobile from '@molecules/StepperNavMobile';
import { BodyContainerStyled } from '@styled';
import SectionBreadcrumbs from '../../../_molecules/SectionBreadcrumbs';
import SectionBreadcrumb from '../../../_molecules/SectionBreadcrumb';
import FooterNavigation from '../../../_molecules/FooterNavigation';

// Functionality
import { LoadingContext } from '@context/LoadingProvider';
import { addMembershipToCart, membershipYears } from '@general/helpers/cart.helpers';
import useCartFromAPI from '@hooks/apiLegacy/useCartFromAPI';
import useMemberFromAPI from '@hooks/apiLegacy/useMemberFromAPI';
import { useScreenMinMd, useScreenSmDown, useScreenMdDown, useScreenMinLg } from '@hooks/useResponsive';
import useViewer from '@hooks/useViewer';
import { getMemberInfoBreadcrumbLink } from '@general/helpers/member.helpers';

const BreadCrumbContainerStyled = styled.div`
    background-color: #f9f9f9;
`;

const MembershipDetails = ({ isNew }) => {
    const { setIsLoading } = useContext(LoadingContext);
    const history = useHistory();
    const formRef = useRef();
    const { viewer } = useViewer();
    const { member: memberFromAPI, refetch: refetchMember } = useMemberFromAPI({ id: viewer?.recordId });
    const { cart, refetch } = useCartFromAPI({ viewerId: viewer?.recordId });
    const isScreenMinMd = useScreenMinMd();
    const isScreenSmDown = useScreenSmDown();
    const isScreenMdDown = useScreenMdDown();
    const isScreenMinLg = useScreenMinLg();

    // If the user is not logged in, redirect them to the start of the membership flow
    useEffect(() => {
        if (viewer === null) {
            history.push('/membership/start');
        }
    }, [viewer, history]);

    useEffect(() => {
        if (viewer && memberFromAPI === null) {
            refetchMember();
        }
    }, [viewer, memberFromAPI, refetchMember]);

    // Redirect the user to the hold page if they have an OFAC error
    useEffect(() => {
        if (isNew || !memberFromAPI) {
            return;
        }

        if (memberFromAPI.ofac_error) {
            history.push('/membership/hold');
        }
    }, [isNew, memberFromAPI, history]);

    if (!viewer || !cart) {
        return null;
    }

    return (
        <ErrorHandler>
            <TrackPageView>
                {isScreenMinMd && (
                    <BreadCrumbContainerStyled>
                        <div className="container">
                            <div className="py-1rem">
                                <SectionBreadcrumbs className="mt-2">
                                    <SectionBreadcrumb isActive>Account Info</SectionBreadcrumb>
                                    <SectionBreadcrumb route="/cart/view">Cart Details</SectionBreadcrumb>
                                    <SectionBreadcrumb route="/cart/payment">Payment Info</SectionBreadcrumb>
                                </SectionBreadcrumbs>
                            </div>
                        </div>
                    </BreadCrumbContainerStyled>
                )}

                {isScreenSmDown && (
                    <StepperNavMobile
                        activeIndex={0}
                        steps={[
                            { label: 'Account Info', href: getMemberInfoBreadcrumbLink(cart, viewer?.recordId) },
                            { label: 'Cart Details', href: '/cart/view' },
                            { label: 'Payment Info', href: '/cart/payment' },
                        ]}
                    />
                )}

                <BodyContainerStyled className="container">
                    <div className="grid-cols-8 md:grid">
                        <div
                            className={classNames({
                                'col-span-7': isScreenMdDown,
                                'col-span-5': isScreenMinLg,
                            })}
                        >
                            <AccountInformationForm
                                disableCompanySelection={isNew}
                                formRef={formRef}
                                memberId={isNew ? null : viewer?.recordId} // TODO: this should always be memberId or null, not the viewerId
                                onMemberSaved={async member => {
                                    await addMembershipToCart({
                                        cart,
                                        memberId: member.id,
                                        viewerId: viewer.recordId,
                                        years: membershipYears.THREE,
                                    });

                                    await refetch();
                                    await refetchMember();

                                    setIsLoading(false);
                                    history.push('/cart/view');
                                }}
                                viewerId={viewer?.recordId}
                            />
                        </div>
                    </div>
                </BodyContainerStyled>

                <FooterNavigation
                    nextLabel="Save & Continue"
                    previousLabel="Cancel"
                    onNextClick={() => {
                        if (formRef.current) {
                            formRef.current.handleSubmit();
                        }
                    }}
                    onPreviousClick={() => history.push('/membership/start')}
                />
            </TrackPageView>
        </ErrorHandler>
    );
};

MembershipDetails.propTypes = {
    isNew: PropTypes.bool.isRequired,
};

export default MembershipDetails;
